<template>
    <div class="grid gap-4 sm:grid-cols-2">
        <article
            v-for="item in media"
            :key="`${ item.type }:${ item.id }`"
            class="rounded border border-primary/30 p-3"
        >
            <div
                v-if="item.status === 'deleted'"
                class="flex h-40 items-center justify-center rounded bg-background-accent text-green-500"
            >
                <checkmark-icon class="mr-2 h-5 w-5 fill-current"/>
                {{ t('deleted') }}
            </div>
            <div
                v-else-if="item.type === 'file' || item.type === 'audio'"
                class="flex h-40 items-center justify-center rounded bg-background-accent"
            >
                <AudioIcon v-if="item.type === 'audio'" class="h-10 w-10 fill-primary" aria-hidden="true"/>
                <FileIcon v-else class="h-10 w-10 fill-primary" aria-hidden="true"/>
            </div>
            <img
                v-else
                :src="previewUrl(item)"
                :alt="t('previewAlt', { name: item.label })"
                class="h-40 w-full rounded bg-background-accent object-contain"
            >
            <div class="mt-2 break-all text-sm font-medium">{{ item.label }}</div>
            <y-button
                class="mt-3 w-full"
                type="destructive"
                border-only
                :disabled="item.status === 'deleting' || item.status === 'deleted'"
                @click.prevent="emit('delete', item)"
            >
                {{ item.status === 'deleting' ? t('deleting') : t('delete') }}
            </y-button>
        </article>
    </div>
</template>

<script setup lang="ts">
defineProps<{
    media: OrphanedMediaOption[]
}>()

const emit = defineEmits<{
    delete: [media: OrphanedMediaOption]
}>()

const { t } = useI18n()

const previewUrl = (media: OrphanedMediaOption) => {
    // b-media entries hold the pointer's own id, not the real b-image/b-video asset's id
    // (see mediaUsage.service.ts) — the media thumbnail endpoint resolves the pointer for us.
    if (media.bucket === 'b-media') return `/api/files/media/thumbnail/${ encodeURIComponent(media.id) }`
    return media.type === 'image'
        ? `/api/files/image/thumbnail/${ encodeURIComponent(media.id) }`
        : `/api/files/video/thumbnail/${ encodeURIComponent(media.id) }`
}
</script>

<i18n lang="yaml">
en:
    previewAlt: Preview of {name}
    delete: Delete this media
    deleting: Deleting…
    deleted: Deleted
nl:
    previewAlt: Voorbeeld van {name}
    delete: Verwijder deze media
    deleting: Verwijderen…
    deleted: Verwijderd
</i18n>
